import { useState } from 'react';
import {
  ConversionFactorToMassModel,
  EPDModel,
  MaterialPropertyModel,
  ReferenceFlowOldWizardModel,
} from 'services/EpdClient';
import EditMachineReadableDialog from '../../dialogs/EditMachineReadableDialog';
import EpdDetailsGrid, { EpdDetailsGridRow } from '../EpdDetailsGrid';
import MachineReadableEPDKey from '../MachineReadableEPDKey';
import { useDictionaryValues, useReferencePackageVersions15804 } from 'services/api/queries';
import { EPDBasedOnUnitEnum, Option } from 'types/types';
import { YES_NO } from 'components/v2/compiler/constants';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { getOptionsFromDictionary } from 'util/utils';

type TProps = {
  epd: EPDModel;
  onChangeEpd(name: string, value: any): Promise<void>;
  isReadOnly: boolean;
  isConstruction?: boolean;
  materialProperties: MaterialPropertyModel[];
  biogenicCarbonContentUnits?: Option[];
};

const MachineReadable = ({
  epd,
  onChangeEpd,
  isReadOnly,
  isConstruction,
  materialProperties,
  biogenicCarbonContentUnits,
}: TProps) => {
  const [isMachineReadableEditOpened, setIsMachineReadableOpened] = useState<boolean>(false);
  const referencePackageVersions15804 = useReferencePackageVersions15804().data;

  const dictionariesQuery = useDictionaryValues();
  const epdBasedOnUnitOptions = getOptionsFromDictionary(dictionariesQuery.data, EPD_DICTIONARY_CODES.EPD_BASED_ON_UNIT);
  const epdBasedOnUnitString = epdBasedOnUnitOptions.find(
    (x) => epd?.epdBasedOnUnit && x.value === Object.keys(EPDBasedOnUnitEnum).indexOf(epd?.epdBasedOnUnit).toString()
  )?.label;

  const flowPropertiesOptions =
    dictionariesQuery.data
      ?.filter((d) => d.dictionaryCode === EPD_DICTIONARY_CODES.REFERENCE_FLOW_UNIT)
      .map((d) => ({ value: d.value.toLowerCase(), label: d.description })) ?? [];

  const renderReferenceFlowData = (productFlow: ReferenceFlowOldWizardModel | undefined) => {
    const flowPropertyName = flowPropertiesOptions.find((x) => x.value == productFlow?.flowPropertyId)?.label;
    const conversionFactor = productFlow?.conversionFactor;
    if (!productFlow?.name || !flowPropertyName || !conversionFactor) {
      return null;
    }
    return `${productFlow?.name}<br />Unit: ${flowPropertyName}, Value: ${conversionFactor}`;
  };

  const renderConversionFactorToMass = (conversionFactorToMass: ConversionFactorToMassModel | undefined) => {
    const conversionFactor = conversionFactorToMass?.conversionFactor;
    if (!conversionFactor) {
      return null;
    }
    return `Value: ${conversionFactor}`;
  };

  const detailsGridData: (EpdDetailsGridRow | null)[] = [
    { key: MachineReadableEPDKey.GTIN, value: epd.gtin },
    { key: MachineReadableEPDKey.EPD_BASED_ON_UNIT, value: epdBasedOnUnitString, required: isConstruction },
    epd?.epdBasedOnUnit === EPDBasedOnUnitEnum.FunctionalUnit
      ? {
          key: MachineReadableEPDKey.FUNCTIONAL_UNIT_DESCRIPTION,
          value: epd?.functionalUnitDescription,
          required: true,
        }
      : null,
    {
      key:
        epd?.epdBasedOnUnit === EPDBasedOnUnitEnum.DeclaredUnit
          ? MachineReadableEPDKey.DECLARED_UNIT_AND_REFERENCE_FLOW
          : MachineReadableEPDKey.REFERENCE_FLOW,
      value: renderReferenceFlowData(epd.referenceFlow),
      isHtml: true,
      required: isConstruction,
    },
    {
      key: MachineReadableEPDKey.CONVERSION_FACTOR_TO_MASS,
      value: renderConversionFactorToMass(epd.conversionFactorToMass),
      required: isConstruction,
    },
    {
      key: MachineReadableEPDKey.MASS_OF_DECLARED_UNIT,
      value: epd.massOfDeclaredUnit ? String(epd.massOfDeclaredUnit) : null,
      required: isConstruction,
    },
    { key: MachineReadableEPDKey.PRODUCT_TO_DECLARE, value: epd?.productType, required: isConstruction },
    {
      key: MachineReadableEPDKey.PRODUCT_CONTAIN_MORE_THAN_5_BIOGENIC_CARBON,
      value: epd?.biogenicCarbonContent?.doesProductContainMoreThan5Percent,
      required: epd?.pcr?.isConstruction,
    },
    {
      key: MachineReadableEPDKey.PACKAGE_CONTAIN_MORE_THAN_5_BIOGENIC_CARBON,
      value: epd?.biogenicCarbonContent?.doesPackagingMaterialContainMoreThan5Percent,
      required: epd?.pcr?.isConstruction,
    },
    {
      key: MachineReadableEPDKey.BIOGENIC_CARBON_CONTENT_OF_PRODUCT,
      value: (() => {
        const cf = epd?.biogenicCarbonContent?.productItem?.conversionFactor;
        if (
          !epd?.biogenicCarbonContent?.productItem?.unitKey ||
          (cf === null || cf === undefined)
        ) {
          return '';
        }
        const unit = biogenicCarbonContentUnits?.find(
          (x) => x.value === epd?.biogenicCarbonContent?.productItem?.unitKey?.toString()
        )?.label;
        return `Unit: ${unit}, Value: ${cf}`;
      })(),
      required: epd?.biogenicCarbonContent?.doesProductContainMoreThan5Percent == YES_NO.YES,
    },
    {
      key: MachineReadableEPDKey.BIOGENIC_CARBON_CONTENT_OF_PACKAGE,
      value: (() => {
        const cf = epd?.biogenicCarbonContent?.accompanyingPackageItem?.conversionFactor;
        if (
          !epd?.biogenicCarbonContent?.accompanyingPackageItem?.unitKey ||
          (cf === null || cf === undefined)
        ) {
          return '';
        }
        const unit = biogenicCarbonContentUnits?.find(
          (x) => x.value === epd?.biogenicCarbonContent?.accompanyingPackageItem?.unitKey?.toString()
        )?.label;
        return `Unit: ${unit}, Value: ${cf}`;
      })(),
      required: epd?.biogenicCarbonContent?.doesPackagingMaterialContainMoreThan5Percent == YES_NO.YES,
    },
    {
      key: MachineReadableEPDKey.MATERIAL_PROPERTIES,
      value: (() => {
        const epdMaterialProperties = epd?.materialProperties;
        if (!epdMaterialProperties) {
          return '';
        }

        const formattedProperties = epdMaterialProperties.map((p) => {
          const propertyNameAndUnit = materialProperties.find((m) => m.id === p.materialPropertyId)?.fullName ?? '-';
          return `Property (Unit): ${propertyNameAndUnit}, Value: ${p.value}`;
        });

        const shouldFormatAsHtmlList = epdMaterialProperties.length >= 2;
        if (shouldFormatAsHtmlList) {
          return formattedProperties.map((p) => `<li>${p}</li>`).join('');
        }

        return formattedProperties.join('');
      })(),
      isHtml: true,
      required: isConstruction && !!epd?.referenceFlow?.flowPropertyId,
      errors: epd?.materialPropertiesState?.errorMessage,
    },
    { key: MachineReadableEPDKey.GEOGRAPHICAL_SCOPE_DESCRIPTION, value: epd.geographicalDescription },
    {
      key: MachineReadableEPDKey.TECHNOLOGY_DESCRIPTION,
      value: epd.technologyDescription,
      required: isConstruction,
    },
    { key: MachineReadableEPDKey.TECHNICAL_PURPOSE, value: epd.technicalPurpose, required: isConstruction },
    { key: MachineReadableEPDKey.SUBTYPE, value: epd.subtype, required: isConstruction },
    {
      key: MachineReadableEPDKey.DATA_SOURCES,
      value: [
        ...(epd.epdDataSources?.map((x) => {
          const useListItem =
            (epd.epdDataSources?.length ?? 0) > 1 ||
            ((epd.epdDataSources?.length ?? 0) == 1 && epd.isOtherDatabaseUsedAsDatasource);
          const content = [x.dataSource?.name, x.dataSourceVersion?.name].filter(Boolean).join(', ');
          return useListItem ? `<li>${content}</li>` : content;
        }) ?? []),
        epd.isOtherDatabaseUsedAsDatasource
          ? (epd.epdDataSources?.length ?? 0) >= 1
            ? `<li>Other database, ${epd.otherDatabaseUsedAsDatasourceDescription ?? ''}</li>`
            : `Other database, ${epd.otherDatabaseUsedAsDatasourceDescription ?? ''}`
          : null,
      ]
        .filter(Boolean)
        .join(''),
      isHtml: true,
      required: isConstruction,
      errors:
        epd?.epdDataSourcesState?.errorMessage ||
        //just hot fix for extra validation for "Other database" description if that option is selected - so there is no need to reload the page
        (epd.isOtherDatabaseUsedAsDatasource && !epd.otherDatabaseUsedAsDatasourceDescription
          ? 'Other database description needed'
          : undefined) ||
        ((epd.epdDataSources || []).filter((x) => x.dataSource?.id).filter((x) => x.dataSourceVersion?.id).length === 0 &&
        epd.pcr?.isConstruction
          ? 'Data sources database and version are required'
          : undefined),
    },
    {
      key: MachineReadableEPDKey.REFERENCE_PACKAGE_VERSION_15804,
      value: referencePackageVersions15804?.versions.find((v) => v.id === epd.referencePackageVersion15804)?.name ?? '',
      required: isConstruction,
    },
    {
      key: MachineReadableEPDKey.CHARACTERISATION_METHODS,
      value: epd.characterisationMethods,
    },
    {
      key: MachineReadableEPDKey.ADDITIONAL_INFORMATION_ABOUT_THE_UNDERLYING_LCA_BASED_INFORMATION,
      value: epd.lcaBasedDescription,
      isHtml: true,
    },
    { key: MachineReadableEPDKey.USE_ADVICE, value: epd.useAdvice, required: isConstruction },
  ];

  return (
    <>
      <EpdDetailsGrid
        headerLabel={'Information for machine-readable EPDs'}
        isReadOnly={isReadOnly}
        data={detailsGridData.filter((d) => d !== null) as EpdDetailsGridRow[]}
        onEdit={() => setIsMachineReadableOpened(true)}
      />
      <EditMachineReadableDialog
        isOpened={isMachineReadableEditOpened}
        isConstruction={isConstruction}
        epd={epd}
        onHide={() => setIsMachineReadableOpened(false)}
        onChangeEpd={onChangeEpd}
        flowPropertiesOptions={flowPropertiesOptions}
        materialProperties={materialProperties}
        biogenicCarbonContentUnits={biogenicCarbonContentUnits}
      />
    </>
  );
};

export default MachineReadable;
